import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2 } from "lucide-react";
import { fetchCollections, ShopifyCollection, getOptimizedShopifyImageUrl } from "@/lib/shopify";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut" as const,
    },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

export const CollectionsSection = () => {
  const [collections, setCollections] = useState<ShopifyCollection[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCollections = async () => {
      try {
        const data = await fetchCollections(6);
        setCollections(data);
      } catch (error) {
        console.error("Failed to fetch collections:", error);
      } finally {
        setIsLoading(false);
      }
    };
    loadCollections();
  }, []);

  if (isLoading) {
    return (
      <section className="section-padding">
        <div className="container-calim flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </section>
    );
  }

  if (collections.length === 0) {
    return null;
  }

  return (
    <section className="section-padding">
      <div className="container-calim">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="text-center mb-12 md:mb-16 max-w-2xl mx-auto"
        >
          <motion.p variants={fadeInUp} className="text-subhead mb-3 md:mb-4">
            Our Collections
          </motion.p>
          <motion.h2 variants={fadeInUp} className="text-headline mb-4 md:mb-6">
            Explore Our Range
          </motion.h2>
          <motion.p variants={fadeInUp} className="text-body-lg text-base md:text-lg lg:text-xl">
            Each collection is designed to address a specific need.
            Find the one that suits you best.
          </motion.p>
        </motion.div>

        {/* Collections Grid */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={staggerContainer}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6"
        >
          {collections.slice(0, 6).map((collection) => (
            <motion.div key={collection.id} variants={fadeInUp}>
              <Link
                to={`/collections/${collection.handle}`}
                className="group block relative overflow-hidden aspect-[4/5]"
              >
                {/* Image - lazy loaded with Shopify CDN resize */}
                {collection.image ? (
                  <img
                    src={getOptimizedShopifyImageUrl(collection.image.url, 500)}
                    alt={collection.image.altText || collection.title}
                    loading="lazy"
                    decoding="async"
                    width={500}
                    height={625}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                ) : (
                  <div className="w-full h-full bg-secondary flex items-center justify-center">
                    <span className="text-muted-foreground text-sm">
                      {collection.title}
                    </span>
                  </div>
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300" />

                {/* Content */}
                <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6">
                  <span className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                    {collection.products.edges.length} product
                    {collection.products.edges.length > 1 ? "s" : ""}
                  </span>
                  <h3 className="font-display text-lg md:text-xl lg:text-2xl text-foreground mb-2 group-hover:text-primary transition-colors">
                    {collection.title}
                  </h3>
                  {collection.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-3 md:mb-4">
                      {collection.description}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                    Discover
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        {collections.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="text-center mt-10 md:mt-12"
          >
            <Link to="/collections" className="btn-calim-secondary">
              View all collections
            </Link>
          </motion.div>
        )}
      </div>
    </section>
  );
};
